import { zodResolver } from '@hookform/resolvers/zod';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form/form';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useAutoSave } from '@/hooks/use-auto-save';
import { cn } from '@/lib/utils';

import { useEditIngredientDetails } from '../api/ingredients-mutations';
import {
  IngredientWithDetails,
  SupplierOption,
} from '../types/ingredient-types';

const detailsFormSchema = z.object({
  code: z.string().min(1, 'Code is required'),
  supplier: z.string().min(1, 'Supplier is required'),
});

type DetailsForm = z.infer<typeof detailsFormSchema>;

export interface IngredientDetailsFormPresentationProps {
  suppliers: SupplierOption[];
  ingredient: IngredientWithDetails;
  onSubmit: (values: DetailsForm) => Promise<void>;
  isEditable: boolean;
}

function IngredientDetailsFormPresentation({
  suppliers,
  ingredient,
  onSubmit,
  isEditable,
}: IngredientDetailsFormPresentationProps) {
  const formattedSuppliers = useMemo(
    () =>
      suppliers.map((supplier) => ({
        value: supplier.id,
        label: supplier.title,
        isManaged: supplier.isManaged,
      })),
    [suppliers],
  );

  const form = useForm<DetailsForm>({
    resolver: zodResolver(detailsFormSchema),
    defaultValues: {
      code: ingredient.code,
      supplier: ingredient.supplier_id,
    },
  });

  useAutoSave(form, onSubmit, (error) => {
    if (error.code === '23505') {
      form.setError('code', {
        type: 'manual',
        message: 'Ingredient code already exists',
      });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="flex justify-between items-center gap-4">
          {/* Code */}
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem className="flex-1">
                <div className="flex items-center justify-between">
                  <FormLabel className="whitespace-nowrap text-md">
                    Code:
                  </FormLabel>
                  <FormMessage className="text-right"></FormMessage>
                </div>
                <FormControl>
                  <Input {...field} disabled={!isEditable} />
                </FormControl>
              </FormItem>
            )}
          />
          {/* Supplier */}
          <FormField
            control={form.control}
            name="supplier"
            render={({ field }) => (
              <FormItem className="flex flex-col flex-1">
                <div className="flex items-center justify-between">
                  <FormLabel className="whitespace-nowrap text-md">
                    Supplier:
                  </FormLabel>
                  <FormMessage></FormMessage>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        role="combobox"
                        disabled={!isEditable}
                        className={cn(
                          'w-full justify-between',
                          !field.value && 'text-muted-foreground',
                        )}
                      >
                        {field.value
                          ? formattedSuppliers.find(
                              (supplier) => supplier.value === field.value,
                            )?.label
                          : 'Select supplier'}
                        <ChevronsUpDown className="opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput
                        placeholder="Search suppliers..."
                        className="h-9"
                      />
                      <CommandList>
                        <CommandEmpty>No supplier found.</CommandEmpty>
                        <CommandGroup>
                          {formattedSuppliers
                            .filter((supplier) => !supplier.isManaged)
                            .map((supplier) => (
                              <CommandItem
                                value={supplier.label}
                                key={supplier.value}
                                onSelect={() => {
                                  form.setValue('supplier', supplier.value, {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                  });
                                }}
                              >
                                {supplier.label}
                                <Check
                                  className={cn(
                                    'ml-auto',
                                    supplier.value === field.value
                                      ? 'opacity-100'
                                      : 'opacity-0',
                                  )}
                                />
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
}

interface IngredientDetailsFormProps {
  ingredient: IngredientWithDetails;
  suppliers: SupplierOption[];
  isEditable: boolean;
}

export function IngredientDetailsForm({
  ingredient,
  suppliers,
  isEditable,
}: IngredientDetailsFormProps) {
  const editIngredientDetails = useEditIngredientDetails();

  const onSubmit = useCallback(
    async (values: DetailsForm) => {
      await editIngredientDetails.mutateAsync({
        id: ingredient.id,
        code: values.code,
        supplier_id: values.supplier,
      });
    },
    [editIngredientDetails, ingredient.id],
  );

  return (
    <IngredientDetailsFormPresentation
      suppliers={suppliers}
      ingredient={ingredient}
      onSubmit={onSubmit}
      isEditable={isEditable}
      key={ingredient.id}
    />
  );
}
